import { UNIT_BATCH_ROW_STATUSES, type UnitBatchResponse } from '@sivan/shared';
import { Modal } from './Modal';
import { OUTCOME_LABELS, and, count, errorText } from './labels';
import type { GridRow } from './pending';

export type ReviewPhase = 'reviewing' | 'saving' | 'uncertain';

interface ReviewDialogProps {
  readonly open: boolean;
  readonly phase: ReviewPhase;
  readonly result: UnitBatchResponse | null;
  readonly rows: readonly GridRow[];
  /** Stored values the batch would clear (empty cells). */
  readonly clearedValues: number;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  readonly onKeepMine: (key: string) => void;
  readonly onDiscard: (key: string) => void;
}

const PROBLEMS = new Set(['invalid', 'duplicate', 'conflict']);

/**
 * Shows the server's classification of the pending batch before anything is written (DR-147),
 * lists every problem row, and confirms the all-or-nothing save.
 */
export function ReviewDialog({
  clearedValues,
  open,
  phase,
  result,
  rows,
  onConfirm,
  onClose,
  onKeepMine,
  onDiscard,
}: ReviewDialogProps) {
  const byKey = new Map(rows.map((row) => [row.key, row]));
  const problems = result?.rows.filter((row) => PROBLEMS.has(row.status)) ?? [];
  const writable = (result?.summary.create ?? 0) + (result?.summary.update ?? 0);
  const canSave = result !== null && problems.length === 0 && writable > 0 && phase !== 'saving';

  return (
    <Modal title="בדיקת השינויים לפני שמירה" open={open} onClose={onClose} wide>
      {result === null ? (
        <p role="status">בודק את השינויים מול השרת…</p>
      ) : (
        <>
          <ul className="summary-chips" aria-label="סיכום השינויים">
            {UNIT_BATCH_ROW_STATUSES.filter((status) => result.summary[status] > 0).map(
              (status) => (
                <li
                  key={status}
                  className={`chip chip--${PROBLEMS.has(status) ? 'error' : status}`}
                >
                  {OUTCOME_LABELS[status]}: {result.summary[status]}
                </li>
              ),
            )}
          </ul>

          {problems.length > 0 && (
            <section aria-labelledby="problems-title">
              <h3 id="problems-title">יש לטפל בשורות הבאות לפני השמירה</h3>
              <table className="problem-table">
                <thead>
                  <tr>
                    <th scope="col">יחידה</th>
                    <th scope="col">מצב</th>
                    <th scope="col">פירוט</th>
                    <th scope="col">פעולה</th>
                  </tr>
                </thead>
                <tbody>
                  {problems.map((problem) => {
                    const row = byKey.get(problem.clientRowId);
                    const name =
                      row === undefined
                        ? ''
                        : `${row.cells.code || '—'} · ${row.cells.displayNumber || '—'}`;
                    return (
                      <tr key={problem.clientRowId}>
                        <td>{name}</td>
                        <td>{OUTCOME_LABELS[problem.status]}</td>
                        <td>
                          {Object.entries(problem.fields)
                            .map(([field, code]) => errorText(field, code))
                            .join(' · ')}
                        </td>
                        <td className="problem-actions">
                          {problem.status === 'conflict' &&
                            problem.fields['rowVersion'] === 'stale' && (
                              <button
                                type="button"
                                className="button button--small"
                                onClick={() => {
                                  onKeepMine(problem.clientRowId);
                                }}
                              >
                                החלת השינוי שלי על הגרסה העדכנית
                              </button>
                            )}
                          <button
                            type="button"
                            className="button button--small button--ghost"
                            onClick={() => {
                              onDiscard(problem.clientRowId);
                            }}
                          >
                            ביטול השינוי בשורה
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="hint">
                תקנו את התאים המסומנים בטבלה ובדקו שוב. השינויים שלכם נשמרים בטיוטה עד לשמירה.
              </p>
            </section>
          )}

          {problems.length === 0 && writable > 0 && phase !== 'uncertain' && (
            <p>
              יישמרו{' '}
              {and(
                count(result.summary.create, 'יחידה חדשה אחת', 'יחידות חדשות'),
                count(result.summary.update, 'עדכון אחד', 'עדכונים'),
              )}
              . השמירה מתבצעת כולה, או לא מתבצעת כלל.
            </p>
          )}
          {problems.length === 0 && writable === 0 && <p>אין שינויים לשמירה.</p>}
          {clearedValues > 0 && (
            <p className="notice notice--warning">
              {count(clearedValues, 'ערך קיים אחד יימחק', 'ערכים קיימים יימחקו')} (תאים שרוקנו
              בעריכה). בדקו שזו הכוונה לפני השמירה.
            </p>
          )}
          {phase === 'uncertain' && (
            <p role="alert" className="notice notice--warning">
              החיבור לשרת נקטע ולא ידוע אם השמירה הושלמה. ניסיון חוזר בטוח: אותה פעולה לא תיכתב
              פעמיים.
            </p>
          )}
        </>
      )}
      <footer className="modal__footer">
        <button
          type="button"
          className="button button--primary"
          disabled={!canSave}
          onClick={onConfirm}
        >
          {phase === 'saving' ? 'שומר…' : phase === 'uncertain' ? 'ניסיון חוזר' : 'שמירה'}
        </button>
        <button type="button" className="button" onClick={onClose} disabled={phase === 'saving'}>
          חזרה לעריכה
        </button>
      </footer>
    </Modal>
  );
}
